import styled from "@emotion/styled";
import { TopBarSlotProvider } from "@ui/organisms/Header/TopBarSlot";
import { ReactNode } from "react";

import { AppTopBar, TOP_BAR_PADDING_X } from "./AppTopBar/AppTopBar";

const Main = styled("main")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  // Without this, wide content (scroll rails, tables) inflates <main>'s
  // min-content width and the whole page overflows instead of scrolling.
  minWidth: 0,
  transition: theme.transitions.create("margin", {
    duration: theme.transitions.duration.enteringScreen,
    easing: theme.transitions.easing.sharp
  })
}));

/**
 * The page's side inset, chosen so its content starts exactly under the top
 * bar's breadcrumbs: the bar pads `TOP_BAR_PADDING_X` from the nav's edge,
 * and `PageContainer` adds 16px of its own inside this column, so the column
 * contributes only the difference — derived here, so it cannot drift.
 *
 * Applied on BOTH sides. It used to be left-only, which left the page 26px
 * from the nav and 16px from the window — the page sat visibly off-centre
 * under a bar whose own two ends were even.
 */
const PAGE_CONTAINER_PAD_X = 16;
const CONTENT_GAP = TOP_BAR_PADDING_X - PAGE_CONTAINER_PAD_X;

const Content = styled("div")({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
  paddingLeft: CONTENT_GAP,
  paddingRight: CONTENT_GAP
});

export interface Props {
  children: ReactNode;
}

/**
 * The content column beside the nav: the top bar, then the page. The bar's
 * action slot is provided here so any `Header` rendered in the page can opt
 * into it — see `TopBarSlot`.
 */
export const MainSectionLayout = ({ children }: Props) => (
  <TopBarSlotProvider>
    <Main>
      <AppTopBar />
      <Content>{children}</Content>
    </Main>
  </TopBarSlotProvider>
);
